/**
 * Offline queue for the Easer portal: field reports (with photos) and incidents are kept on the
 * device (IndexedDB) when the network is down, then sent automatically when it comes back.
 */
import { easerPost, isDolibarrUnavailable } from './easerApi';

const DB_NAME = 'easer-offline';
const STORE = 'queue';
const listeners = new Set();
let memoryFallback = []; // used when IndexedDB is unavailable (private mode, old browsers)
let flushing = false;

const openDb = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') return reject(new Error('no-idb'));
  const req = indexedDB.open(DB_NAME, 1);
  req.onupgradeneeded = () => { req.result.createObjectStore(STORE, { keyPath: 'id' }); };
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const withStore = async (mode, fn) => {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      tx.oncomplete = () => { db.close(); resolve(req?.result); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch {
    return undefined; // caller falls back to memory
  }
};

const notify = async () => {
  const items = await listQueue();
  listeners.forEach((cb) => { try { cb(items); } catch { /* listener error must not break the queue */ } });
};

/** Subscribe to queue changes; returns an unsubscribe function. */
export const subscribeQueue = (cb) => { listeners.add(cb); listQueue().then(cb); return () => listeners.delete(cb); };

export const listQueue = async () => {
  const rows = await withStore('readonly', (s) => s.getAll());
  const items = Array.isArray(rows) ? rows : memoryFallback;
  return [...items].sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
};

export const queueCount = async () => (await listQueue()).length;

/** Axios error that means "no network" (not a server answer). */
export const isNetworkError = (err) => !err?.response && (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error' || (typeof navigator !== 'undefined' && navigator.onLine === false));

/**
 * Faut-il garder l'action sur le téléphone pour la rejouer plus tard ?
 * Oui sans réseau, et oui quand le serveur répond que Dolibarr lui-même est indisponible (mode dégradé).
 */
export const shouldQueue = (err) => isNetworkError(err) || isDolibarrUnavailable(err);

/** Raison de la mise en file, pour les messages : 'offline' | 'dolibarr'. */
export const queueReason = (err) => (isNetworkError(err) ? 'offline' : 'dolibarr');

/** Phrase d'explication à afficher avec le toast de mise en file. */
export const queuedBecause = (err, what) => (isNetworkError(err)
  ? `Pas de réseau : ${what} gardé(e) sur le téléphone, envoi automatique au retour de la connexion`
  : `Dolibarr indisponible : ${what} gardé(e) sur le téléphone, envoi automatique dès le retour de Dolibarr`);

/**
 * Queue an action. type: 'report' | 'incident' | 'expense' | 'clock_start' | 'clock_end' | 'clock_pause' | 'clock_resume'.
 * payload is the request body.
 * Keeps the device time so the agency sees when it really happened.
 */
export const enqueue = async ({ type, eventId, label, payload }) => {
  const item = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type, eventId, label, payload: { ...payload, at: new Date().toISOString() },
    createdAt: new Date().toISOString(), attempts: 0, lastError: '',
  };
  const ok = await withStore('readwrite', (s) => s.put(item));
  if (ok === undefined) memoryFallback.push(item);
  await notify();
  return item;
};

export const removeFromQueue = async (id) => {
  const ok = await withStore('readwrite', (s) => s.delete(id));
  if (ok === undefined) memoryFallback = memoryFallback.filter((i) => i.id !== id);
  await notify();
};

const updateItem = async (item) => {
  const ok = await withStore('readwrite', (s) => s.put(item));
  if (ok === undefined) memoryFallback = memoryFallback.map((i) => (i.id === item.id ? item : i));
};

const endpoint = (item) => {
  if (item.type === 'report') return `/api/easer/events/${item.eventId}/report`;
  if (item.type === 'expense') return '/api/easer/expenses';
  if (item.type === 'evacuation') return `/api/easer/events/${item.eventId}/evacuation`;
  if (item.type === 'acc_stop') return `/api/easer/events/${item.eventId}/acc/stop`;
  if (isClockItem(item)) return `/api/easer/events/${item.eventId}/clock`;
  return `/api/easer/events/${item.eventId}/incident`;
};

export const TYPE_LABELS = { report: 'Compte-rendu', expense: 'Note de frais', incident: 'Imprévu', evacuation: 'Enlèvement déchets verts', acc_stop: 'Fin d\'accompagnement (km)', clock_start: 'Début de prestation', clock_end: 'Fin de prestation', clock_pause: 'Pause', clock_resume: 'Reprise de prestation' };
export const CLOCK_TYPES = { start: 'clock_start', end: 'clock_end', pause: 'clock_pause', resume: 'clock_resume' };
export const isClockItem = (item) => Object.values(CLOCK_TYPES).includes(item.type);

const clockItems = (items, type) => items.filter((i) => i.type === type).map((i) => ({ eventId: i.eventId, label: i.label, at: i.payload?.at || i.createdAt }));

/** Event ids whose "fin de prestation" is waiting on the device (the lock is lifted locally meanwhile). */
export const pendingClockEndIds = (items) => items.filter((i) => i.type === 'clock_end').map((i) => i.eventId);

/** Starts waiting on the device: [{eventId, label, at}] — the portal locks itself locally on them. */
export const pendingClockStarts = (items) => clockItems(items, 'clock_start');
/** Pauses waiting on the device: the portal shows the slot "en pause" (lock lifted) meanwhile. */
export const pendingClockPauses = (items) => clockItems(items, 'clock_pause');
/** Resumes waiting on the device: the portal locks itself again on the slot meanwhile. */
export const pendingClockResumes = (items) => clockItems(items, 'clock_resume');
/** Event ids with any pointage still waiting on the device: later pointages must be queued too to keep the order. */
export const pendingClockEventIds = (items) => [...new Set(items.filter(isClockItem).map((i) => i.eventId))];

/** Let the page refresh the "intervention en cours" state after a pointage was (re)transmitted. */
export const notifyClockChanged = () => { try { window.dispatchEvent(new Event('easer:clock-changed')); } catch { /* SSR / old browsers */ } };

/**
 * Try to send everything. Returns { sent, failed, remaining, stoppedOffline, dolibarrDown, unauthorized }.
 * Stops at the first network error (still offline) or when the server reports Dolibarr unavailable. Drops items the server rejects for good (4xx)
 * so a bad item never blocks the others; keeps them on 401 (needs a new login) and 5xx (retry later).
 */
export const flushQueue = async () => {
  const result = { sent: 0, failed: 0, remaining: 0, stoppedOffline: false, dolibarrDown: false, unauthorized: false, dropped: [], clockEnds: [] };
  if (flushing) return result;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) { result.stoppedOffline = true; result.remaining = await queueCount(); return result; }
  flushing = true;
  try {
    const items = await listQueue();
    for (const item of items) {
      try {
        const res = await easerPost(endpoint(item), item.payload);
        await removeFromQueue(item.id);
        result.sent += 1;
        if (isClockItem(item)) { result.clockEnds.push({ item, data: res.data }); notifyClockChanged(); }
      } catch (err) {
        if (isNetworkError(err)) { result.stoppedOffline = true; break; }
        if (isDolibarrUnavailable(err)) { result.dolibarrDown = true; break; } // Dolibarr en panne : on garde tout, nouvel essai à son retour
        const status = err?.response?.status;
        if (status === 401) { result.unauthorized = true; break; }
        if (isClockItem(item) && status === 409 && /déjà pointée?/i.test(err?.response?.data?.detail || '')) {
          // Already recorded in Dolibarr (e.g. clocked from the agency module meanwhile): nothing lost.
          await removeFromQueue(item.id);
          result.sent += 1;
          result.clockEnds.push({ item, data: { verified: true, already: true } });
          notifyClockChanged();
          continue;
        }
        if (status >= 400 && status < 500) {
          await removeFromQueue(item.id);
          result.dropped.push({ item, reason: err?.response?.data?.detail || `Erreur ${status}` });
          result.failed += 1;
          continue;
        }
        await updateItem({ ...item, attempts: item.attempts + 1, lastError: String(err?.message || status || 'erreur') });
        result.failed += 1;
      }
    }
  } finally {
    flushing = false;
  }
  result.remaining = await queueCount();
  await notify();
  return result;
};
